import { Button, Tooltip } from "@almafintech/react-components";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Document, Page } from "react-pdf";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../app/hooks";
import DownloadIcon from "../../assets/images/icons/ui/download-outline-blue.svg?react";
import {
  acceptDocuments,
  createAccount,
} from "../../connectors/interactiveBrokers";
import {
  onUpdateCompletedForm,
  onUpdateStep,
  onUpdateSubStep,
} from "../../reducers/onboardingDataReducer";
import { TermsOfServiceDocument } from "../../types/interactiveBrokers";
import { gzippedBase64ToString } from "../../utils/interactiveBrokers";
import Layout from "../Layout/Layout";
import styles from "./TermsOfServicePage.module.scss";
import { texts } from "./i18n";
import Header from "./ui/Header/Header";
import Outline from "./ui/Outline/Outline";

const TermsOfServicePage = () => {
  const { layout, header, container, body, outline } = styles;

  const pageRef = useRef<HTMLDivElement>(null);
  const htmlDocumentIframeRef = useRef<HTMLIFrameElement>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const state = useAppSelector((state) => state);

  const [currentDocument, setCurrentDocument] =
    useState<TermsOfServiceDocument>();
  const [acceptedDocuments, setAcceptedDocuments] = useState<
    TermsOfServiceDocument[]
  >([]);
  const [pages, setPage] = useState<number>();
  const [hasScrolledAllTheWay, setHasScrolledAllTheWay] = useState(false);
  const [isAcceptButtonLoading, setIsAcceptButtonLoading] = useState(false);
  const [currentDocumentHtmlContent, setCurrentDocumentHtmlContent] = useState<
    string | null
  >(null);

  const {
    onboardingData: {
      language = "es",
      stepData: { step },
    },
    interactiveBrokers: { documentsToAccept: documents, personalInformation },
  } = state;

  const handleOutlineDocumentClicked = (document: TermsOfServiceDocument) =>
    acceptedDocuments.includes(document) && setCurrentDocument(document);

  const handleDocumentBackClicked = () =>
    setCurrentDocument(
      documents[documents.findIndex((document) => document === currentDocument)]
    );

  const handleDownloadClicked = () => {
    if (currentDocument) {
      const link = document.createElement("a");
      link.href = currentDocument.url;
      link.setAttribute("target", "blank");
      link.click();
    }
  };

  const handleDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setPage(numPages);
  };

  const handlePdfDocumentScrolled = () => {
    if (pageRef?.current) {
      const isFullyScrolled =
        pageRef?.current &&
        pageRef.current?.scrollHeight - pageRef.current.scrollTop ===
          pageRef.current.clientHeight;

      setHasScrolledAllTheWay((prev) => prev || isFullyScrolled);
    }
  };

  const handleHtmlDocumentScrolled = (event: Event) => {
    const target = (event.target as Document).documentElement;
    const isFullyScrolled =
      Math.abs(target.scrollHeight - target.clientHeight - target.scrollTop) <=
      1;
    setHasScrolledAllTheWay((prev) => prev || isFullyScrolled);
  };

  const handleAcceptClicked = () => {
    if (!currentDocument) return;

    if (pageRef?.current) pageRef.current.scrollTop = 0;
    setHasScrolledAllTheWay(false);
    setAcceptedDocuments((prev) => [...prev, currentDocument]);
    setCurrentDocument(documents[documents.indexOf(currentDocument) + 1]);
  };

  const handleSubmitDocuments = async () => {
    try {
      setIsAcceptButtonLoading(true);

      if (!personalInformation) throw new Error();

      await acceptDocuments({
        user_id: personalInformation?.id,
        accepted_documents: acceptedDocuments.map((document) => ({
          final_document: document.id,
          acceptance_timestamp: new Date().toISOString(),
        })),
      });

      navigate("/apertura-exitosa?redirecting=true");

      const {
        data: { sso_url: url },
      } = await createAccount({ user_id: personalInformation?.id });

      // HACK: This should be on some reducer
      navigate(`/apertura-exitosa?redirectedTo=${encodeURI(url)}`);

      // IBKR redirection on new tab
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("target", "blank");
      link.click();
    } catch {
      navigate("/error?error=INTERACTIVE_BROKERS_ERROR");
    } finally {
      setIsAcceptButtonLoading(false);
    }
  };

  const handleBackClicked = () => {
    dispatch(onUpdateSubStep(1));
    dispatch(onUpdateStep(3));
  };

  const handleEditClicked = () => {
    dispatch(onUpdateSubStep(1));
    dispatch(onUpdateStep(2));
  };

  // Go back to form if step changes
  useEffect(() => {
    if (step !== 4) {
      dispatch(onUpdateCompletedForm(false));
      navigate("/interactive-brokers");
    }
  }, [step]);

  useEffect(() => {
    setCurrentDocument(documents?.[0]);
  }, []);

  // Submit all accepted documents when last one is accepted
  useEffect(() => {
    if (acceptedDocuments.length === documents.length) handleSubmitDocuments();
  }, [acceptedDocuments]);

  // When document is selected, try getting its HTML encoded content if available
  useEffect(() => {
    (async () => {
      const content = await gzippedBase64ToString(currentDocument?.encodedHtml);
      setCurrentDocumentHtmlContent(content);
    })();
  }, [currentDocument]);

  // FIXME: For some reason, doesn't add listener until re-render
  useEffect(() => {
    setHasScrolledAllTheWay(true); // Remove when this is fixed

    const iframeContent = htmlDocumentIframeRef?.current?.contentDocument;

    if (!!currentDocumentHtmlContent && iframeContent) {
      iframeContent.addEventListener("scroll", handleHtmlDocumentScrolled);

      return () =>
        iframeContent.removeEventListener("scroll", handleHtmlDocumentScrolled);
    }
  }, [
    currentDocumentHtmlContent,
    htmlDocumentIframeRef?.current?.contentDocument,
  ]);

  // Check if document is one page long upon loading, in which case
  // `hasScrolledAllTheWay` should be manually set
  useLayoutEffect(() => {
    // HACK: Newly loaded pages are not rendering before `useLayoutEffect` call,
    // so `clientHeight` is 0 and `hasScroll` always comes up false
    setTimeout(() => {
      if (pageRef?.current) {
        const hasScroll =
          pageRef.current.scrollHeight > pageRef.current.clientHeight;
        setHasScrolledAllTheWay(!hasScroll);
      }
    }, 100);
  }, [pages]);

  return (
    <Layout hideSupportButton className={layout}>
      <div className={`flex flex-col gap-3 flex-grow ${container}`}>
        <Header
          personalInformation={personalInformation}
          onBackClicked={handleBackClicked}
          onEditClicked={handleEditClicked}
          language={language}
        />

        <div className="w-full flex flex gap-3 flex-grow">
          <Outline
            documents={documents}
            currentDocument={currentDocument}
            acceptedDocuments={acceptedDocuments}
            onDocumentClicked={handleOutlineDocumentClicked}
            className={outline}
            language={language}
          />

          <div className={`flex flex-col gap-4 flex-grow w-full ${body}`}>
            {!!currentDocument && (
              <>
                <div className={header}>
                  <h1>{currentDocument.name}</h1>
                  <DownloadIcon onClick={handleDownloadClicked} />
                </div>

                {!currentDocumentHtmlContent && (
                  <Document
                    file={currentDocument.url}
                    className="flex-grow"
                    onLoadSuccess={handleDocumentLoadSuccess}
                  >
                    <div
                      className="overflow-y-auto h-full overflow-x-hidden"
                      style={{ maxHeight: "50vh" }} // HACK
                      ref={pageRef}
                      onScroll={handlePdfDocumentScrolled}
                    >
                      {pages &&
                        Array.from(
                          { length: pages },
                          (_, index) => index + 1
                        ).map((page) => <Page key={page} pageNumber={page} />)}
                    </div>
                  </Document>
                )}

                {!!currentDocumentHtmlContent && (
                  <iframe
                    className="w-full h-full"
                    ref={htmlDocumentIframeRef}
                    srcDoc={currentDocumentHtmlContent}
                  />
                )}

                <div className="w-full flex gap-4 justify-end">
                  {currentDocument != documents?.[0] && (
                    <Button
                      text={texts[language].backButton}
                      variant="secondary"
                      onClick={handleDocumentBackClicked}
                    />
                  )}

                  {!hasScrolledAllTheWay && (
                    <Tooltip content={texts[language].acceptButtonTooltip}>
                      <Button
                        text={texts[language].acceptButton}
                        disabled={true}
                      />
                    </Tooltip>
                  )}

                  {hasScrolledAllTheWay && (
                    <Button
                      text={texts[language].acceptButton}
                      onClick={handleAcceptClicked}
                      isLoading={isAcceptButtonLoading}
                    />
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default TermsOfServicePage;
